import { LitElement } from "@lit";
import { t } from "@nova/utils";

class SeamGuidelinesBanner extends LitElement {
  static properties = {
    seamThresholdInch: { type: Number, attribute: "seam-threshold-inch" },
    _inputWidth: { type: Number, state: true },
    _inputLength: { type: Number, state: true },
  };

  constructor() {
    super();
    this._inputWidth = 0;
    this._inputLength = 0;
  }

  connectedCallback() {
    super.connectedCallback();

    this.customCalculatorValueEl = document.querySelector(
      "nova-custom-calculator-values",
    );

    this.customCalculatorValueEl?.addEventListener(
      "custom-calculator:values-updated",
      this.onCalculatorValuesUpdated,
    );

    this.unitSelector = document.querySelector(".js-custom-size-unit-type");
    this.unitSelector?.addEventListener("sl-change", this.resetDisplay);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.customCalculatorValueEl?.removeEventListener(
      "custom-calculator:values-updated",
      this.onCalculatorValuesUpdated,
    );

    this.unitSelector?.removeEventListener("sl-change", this.resetDisplay);
  }

  willUpdate(changedProperties) {
    if (
      changedProperties.has("_inputWidth") ||
      changedProperties.has("_inputLength")
    ) {
      const shouldShow =
        this._inputLength > this.seamThresholdInch &&
        this._inputWidth > this.seamThresholdInch;
      this.hidden = !shouldShow;
    }
  }

  onCalculatorValuesUpdated = (event) => {
    if (!event.detail) return;
    const { value, type } = event?.detail;

    if (type == "w") {
      this._inputWidth = value;
    } else if (type == "h") {
      this._inputLength = value;
    }
  };

  resetDisplay = () => {
    this._inputWidth = 0;
    this._inputLength = 0;
  };

  createRenderRoot() {
    return this;
  }
}

if (typeof customElements.get("seam-guidelines-banner") === "undefined") {
  customElements.define("seam-guidelines-banner", SeamGuidelinesBanner);
}

class SeamGuidelinesDialogButton extends LitElement {
  constructor() {
    super();
  }

  dialogButton = null;

  dialogEl = null;

  connectedCallback() {
    super.connectedCallback();

    this.dialogButton = this.querySelector("button");
    if (!this.dialogButton) return;
    const dialogId = this.dialogButton.getAttribute("aria-controls");
    this.dialogEl = document.querySelector(`#${dialogId}`);

    this.dialogButton?.addEventListener(
      "click",
      this.handleDialogButton.bind(this),
    );
    this.dialogEl?.addEventListener(
      "sl-after-show",
      this.handleDialogButtonStatus.bind(this),
    );
    this.dialogEl?.addEventListener(
      "sl-after-hide",
      this.handleDialogButtonStatus.bind(this),
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.dialogButton?.removeEventListener("click", this.handleDialogButton);
    this.dialogEl?.removeEventListener(
      "sl-after-show",
      this.handleDialogButtonStatus,
    );
    this.dialogEl?.removeEventListener(
      "sl-after-hide",
      this.handleDialogButtonStatus,
    );
  }

  handleDialogButton(event) {
    this.dialogEl.show();
  }

  handleDialogButtonStatus() {
    const isOpen = this.dialogEl.hasAttribute("open");
    this.dialogButton.setAttribute("aria-expanded", String(isOpen));
  }

  createRenderRoot() {
    return this;
  }
}

if (
  typeof customElements.get("seam-guidelines-dialog-button") === "undefined"
) {
  customElements.define(
    "seam-guidelines-dialog-button",
    SeamGuidelinesDialogButton,
  );
}

class SeamGuidelinesDialogInputGroup extends LitElement {
  /**
   * @type {HTMLInputElement[] | null}
   */
  dialogInputFields = null;

  /**
   * @type {HTMLInputElement[] | null}
   */
  customValueInputFields = null;

  /**
   * @type {HTMLInputElement[] | null}
   */
  seamGuidelinesDialog = null;

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();

    this.customCalculatorValueEl = document.querySelector(
      "nova-custom-calculator-values",
    );

    this.seamGuidelinesDialog = this.closest("sl-drawer");
    this.seamGuidelinesDialog.addEventListener(
      "sl-show",
      this.handleDrawerOpen,
    );

    this.customValueInputFields = this.customCalculatorValueEl?.inputFields;
    this.customValueInputFields?.forEach((input) => {
      input?.addEventListener("sl-change", this.handleCustomValueChange);
    });

    this.dialogInputFields = this.querySelectorAll("sl-input");
    this.dialogInputFields?.forEach((input) => {
      input.addEventListener("sl-change", this.handleInputChange.bind(this));
    });

    this.customCalculatorValueEl.addEventListener(
      "custom-calculator:values-swapped",
      this.syncValues.bind(this),
    );

    this.unitSelector = document.querySelector(".js-custom-size-unit-type");
    this.unitSelector?.addEventListener(
      "sl-change",
      this.updateUnitType.bind(this),
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.seamGuidelinesDialog.removeEventListener(
      "sl-show",
      this.handleDrawerOpen,
    );

    this.customValueInputFields?.forEach((input) => {
      input.removeEventListener("sl-change", this.handleCustomValueChange);
    });

    this.dialogInputFields?.forEach((input) => {
      input.removeEventListener("sl-change", this.handleInputChange);
    });

    this.customCalculatorValueEl.removeEventListener(
      "custom-calculator:values-swapped",
      this.syncValues,
    );

    this.unitSelector?.removeEventListener("sl-change", this.updateUnitType);
  }

  findInputByName(inputs, name) {
    return [...inputs]?.find((input) => {
      return input.name == name;
    });
  }

  handleDrawerOpen = () => {
    this.customValueInputFields = this.customCalculatorValueEl?.inputFields;

    this.syncValues();
  };

  handleCustomValueChange = (event) => {
    const inputName = event.target?.name;

    const dialogInput = this.findInputByName(this.dialogInputFields, inputName);
    if (!dialogInput) return;

    dialogInput.value = event.target?.value;
  };

  handleInputChange(event) {
    const inputName = event.target?.name;

    const customInput = this.findInputByName(
      this.customValueInputFields,
      inputName,
    );

    customInput.value = event.target?.value;

    customInput.dispatchEvent(new CustomEvent("sl-blur"));
  }

  syncValues() {
    this.customValueInputFields.forEach((input) => {
      const inputName = input.name;
      const matchedDialogInput = this.findInputByName(
        this.dialogInputFields,
        inputName,
      );

      matchedDialogInput.value = input.value;
    });
  }

  updateUnitType(event) {
    const value = event?.target.value;

    if (!value) return;

    this.setAttribute("data-unit", value);

    this.dialogInputFields?.forEach((input) => {
      input.value = "";
    });
  }

  createRenderRoot() {
    return this;
  }
}

if (typeof customElements.get("seam-guidelines-input-group") === "undefined") {
  customElements.define(
    "seam-guidelines-input-group",
    SeamGuidelinesDialogInputGroup,
  );
}
